import React from "react";
import noop from "lodash/noop";
import { createRegisterNode } from "./RegisterTree.js";
import { useConstant } from "../utils/useConstant.js";
import { useOptionallyControlledHook } from "../utils/useOptionallyControlledHook.js";
import { useGlobalEffect } from "../utils/useGlobalEffect.js";
import { activeBranchStackInitEffect, activeBranchStack, ModalRegisterTreeContext, rootRegisterNode } from "../globals.js";
import { useEventTargetSubscription } from "../utils/useEventTargetSubscription.js";
import styles from "./styles.css";
export const useModalRootRegisterNode = () => useEventTargetSubscription(rootRegisterNode, 'change');
const useActiveRegisterStack = () => {
  useGlobalEffect(activeBranchStackInitEffect);
  return useEventTargetSubscription(activeBranchStack, 'change');
};

/**
 * Use nearest ModalRegister
 */
export const useModalRegister = () => React.useContext(ModalRegisterTreeContext);
export const useModalRegisterInit = _ref => {
  let {
    enabled = true,
    parent: controlledParent
  } = _ref;
  const parent = useOptionallyControlledHook(controlledParent, useModalRegister);
  const activeRegisterStack = useActiveRegisterStack();
  const registerNode = useConstant(createRegisterNode);
  const activeBranchTop = activeRegisterStack.peek();
  const isVisible = activeRegisterStack.has(registerNode);
  const isActive = activeBranchTop === registerNode;
  const level = parent.level + 1;
  const state = React.useMemo(() => ({
    level,
    isActive,
    isVisible
  }), [level, isActive, isVisible]);
  React.useEffect(() => {
    if (enabled) {
      parent.registerNode.addChild(registerNode);
      return () => {
        parent.registerNode.removeChild(registerNode);
      };
    }
    return noop;
  }, [enabled]);
  const register = React.useMemo(() => ({
    ...state,
    registerNode
  }), [state, registerNode]);
  return register;
};
/**
 * Component that creates a modal register
 *
 * All modal components must be wrapped in a ModalRegister to properly
 * manage modal visibility.
 *
 * Supports nesting modals and queing neighboring modals
 */
export const ModalRegister = _ref2 => {
  let {
    enabled = true,
    parentRegister,
    register,
    children
  } = _ref2;
  const thisRegister = useOptionallyControlledHook(register, useModalRegisterInit, {
    enabled,
    parent: parentRegister
  });
  const defaultContainerProps = {
    className: styles['modal-container'],
    style: {
      '--union-modal-level': thisRegister.level,
      '--union-modal-visibility': thisRegister.isVisible ? 'visible' : 'hidden'
    }
  };
  return /*#__PURE__*/React.createElement(ModalRegisterTreeContext.Provider, {
    value: thisRegister
  }, typeof children === 'function' ? children(thisRegister, defaultContainerProps) : children);
};

/**
 * Use the level of the nearest ModalRegister
 */
export const useModalLevel = () => {
  const {
    level
  } = useModalRegister();
  return level;
};

/**
 * Use the active state of the nearsed ModalRegister
 */
export const useModalIsActive = () => {
  const {
    isActive
  } = useModalRegister();
  return isActive;
};